import { Fragment, ReactNode } from "react";
import styled, { css } from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminFontFamily, adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Banner } from "@pages/AdminDashboard/ui/Banner";
import { Button } from "@pages/AdminDashboard/ui/Button";

import { PhoneCardList } from "./PhoneCardList";
import { VisuallyHidden } from "./visuallyHidden";

const { color, density, radius, shadow, typeScale } = adminTokens;
const [, spaceSm, , spaceLg, , spaceXl] = adminTokens.spacing;

// The design's tables: header cells pad 10px above and below, every cell pads
// 16px at each side, and the empty-state card pads 40px above and below.
const headerPaddingY = 10;
const cellPaddingX = spaceLg;
const footerPaddingY = 12;
const emptyCardPaddingY = 40;
const emptyTitleSize = "22px";
const skeletonRowCount = 5;
const skeletonBarHeight = 12;
const skeletonBarWidth = "60%";
const headerSpacing = "0.08em";
const footerGap = 10;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceLg}px;
`;

// Positioned so the screen-reader-only text inside it is clipped with the rest.
const Surface = styled.div`
  position: relative;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.card}px;
  box-shadow: ${shadow.card};
  overflow: hidden;
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
  line-height: ${typeScale.body.lineHeight};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

// Positioned so the screen-reader-only text inside it is clipped with the rest.
const PhoneStack = styled(Stack)`
  position: relative;
`;

const alignmentStyle = css<{ $isAlignedToEnd: boolean }>`
  text-align: ${({ $isAlignedToEnd }) => ($isAlignedToEnd ? "right" : "left")};
`;

const HeaderCell = styled.th<{ $isAlignedToEnd: boolean }>`
  ${alignmentStyle}
  padding: ${headerPaddingY}px ${cellPaddingX}px;
  background-color: ${color.warmGray};
  color: ${color.inkSoft};
  font-family: ${typeScale.eyebrow.fontFamily};
  font-size: ${typeScale.eyebrow.fontSize};
  font-weight: normal;
  letter-spacing: ${headerSpacing};
  text-transform: ${typeScale.eyebrow.textTransform};
`;

const BodyCell = styled.td<{ $isAlignedToEnd: boolean; $isTinted: boolean }>`
  ${alignmentStyle}
  height: ${density.tableRowHeight}px;
  padding: 0 ${cellPaddingX}px;
  border-top: 1px solid ${color.border};
  background-color: ${({ $isTinted }) => ($isTinted ? color.canvas : color.surface)};
  vertical-align: middle;
  white-space: ${({ $isAlignedToEnd }) => ($isAlignedToEnd ? "nowrap" : "normal")};
`;

const DetailCell = styled.td`
  padding: ${spaceSm}px ${cellPaddingX}px ${spaceLg}px;
  border-top: 1px solid ${color.border};
  background-color: ${color.canvas};
`;

const BodyRow = styled.tr`
  &:hover > td {
    background-color: ${color.canvas};
  }
`;

const SkeletonBar = styled.span`
  display: block;
  width: ${skeletonBarWidth};
  height: ${skeletonBarHeight}px;
  border-radius: ${radius.input}px;
  background-color: ${color.warmGray};
`;

// On a phone the footer sits under the cards: the range takes its own line and the
// two Buttons share the next one (PHONE-R25).
const Footer = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex-wrap: ${({ $isPhone }) => ($isPhone ? "wrap" : "nowrap")};
  align-items: center;
  gap: ${footerGap}px;
  padding: ${({ $isPhone }) => ($isPhone ? "0" : `${footerPaddingY}px ${cellPaddingX}px`)};
  border-top: ${({ $isPhone }) => ($isPhone ? "0" : `1px solid ${color.border}`)};

  ${({ $isPhone }) =>
    $isPhone &&
    css`
      & > button {
        flex: 1;
      }
    `}
`;

const Range = styled.span<{ $isPhone: boolean }>`
  flex: ${({ $isPhone }) => ($isPhone ? "1 0 100%" : "1")};
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const EmptyCard = styled.div.attrs({ role: "status" })`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  padding: ${emptyCardPaddingY}px ${spaceXl}px;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.card}px;
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
  text-align: center;
`;

const EmptyTitle = styled.span`
  font-family: ${adminFontFamily.light};
  font-size: ${emptyTitleSize};
`;

const EmptyMessage = styled.span`
  color: ${color.inkSoft};
`;

const EmptyAction = styled.div`
  margin-top: ${spaceSm}px;
`;

export interface DataTableColumn<Row> {
  id: string;
  header: string;
  render: (row: Row) => ReactNode;
  align?: "start" | "end";
  // For a column whose header would only repeat what its Buttons say.
  isHeaderHidden?: boolean;
  // On a phone, "badge" sets the column beside the card's heading (PHONE-R19).
  phonePlacement?: "badge";
}

export interface DataTableEmptyState {
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export interface DataTableError {
  title: string;
  body: string;
  retryLabel: string;
  onRetry: () => void;
}

export interface DataTablePagination {
  offset: number;
  pageSize: number;
  orderLabel: string;
  previousLabel?: string;
  nextLabel?: string;
  hasNextPage?: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

export interface DataTableProps<Row> {
  label: string;
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string;
  getRowTestId?: (row: Row) => string;
  // A panel under its row, spanning every column; null shows none.
  renderRowDetail?: (row: Row) => ReactNode;
  emptyState: DataTableEmptyState;
  pagination?: DataTablePagination;
  selectedRowKey?: string;
  isLoading?: boolean;
  error?: DataTableError;
}

const skeletonRows = Array.from({ length: skeletonRowCount }, (_, index) => index);

interface PaginationFooterProps {
  pagination: DataTablePagination;
  rowCount: number;
  isLoading: boolean;
  isPhone: boolean;
}

const PaginationFooter = ({ pagination, rowCount, isLoading, isPhone }: PaginationFooterProps) => {
  const {
    offset,
    pageSize,
    orderLabel,
    previousLabel = "Previous",
    nextLabel = "Next",
    hasNextPage = rowCount >= pageSize,
    onPrevious,
    onNext,
  } = pagination;

  return (
    <Footer $isPhone={isPhone}>
      <Range $isPhone={isPhone}>
        {rowCount > 0 && !isLoading && `Rows ${offset + 1}–${offset + rowCount} · ${orderLabel}`}
      </Range>
      <Button look="secondary" disabled={isLoading || offset === 0} onClick={() => onPrevious()}>
        <span aria-hidden="true">← </span>
        {previousLabel}
      </Button>
      <Button look="secondary" disabled={isLoading || !hasNextPage} onClick={() => onNext()}>
        {nextLabel}
        <span aria-hidden="true"> →</span>
      </Button>
    </Footer>
  );
};

export const EmptyStateCard = ({ title, message, actionLabel, onAction }: DataTableEmptyState) => (
  <EmptyCard>
    <EmptyTitle>{title}</EmptyTitle>
    {message && <EmptyMessage>{message}</EmptyMessage>}
    {actionLabel && (
      <EmptyAction>
        <Button look="secondary" onClick={onAction}>
          {actionLabel}
        </Button>
      </EmptyAction>
    )}
  </EmptyCard>
);

const ErrorBanner = ({ title, body, retryLabel, onRetry }: DataTableError) => (
  <Banner
    tone="error"
    title={title}
    body={body}
    action={{ label: retryLabel, onAction: onRetry }}
  />
);

// The rows of one search or report: the header, the rows, the pagination footer
// and, in place of them, the empty, loading and failed states (TABLE-R1…R22).
// Zero rows is never a blank table (XCUT-R2); a failure is never silent (XCUT-R3).
export const DataTable = <Row,>({
  label,
  columns,
  rows,
  getRowKey,
  getRowTestId,
  renderRowDetail,
  emptyState,
  pagination,
  selectedRowKey,
  isLoading = false,
  error,
}: DataTableProps<Row>) => {
  const isPhone = useIsPhoneLayout();
  const hasRows = rows.length > 0;
  const shownError = isLoading ? undefined : error;

  if (!isLoading && !hasRows && shownError) return <ErrorBanner {...shownError} />;
  if (!isLoading && !hasRows) return <EmptyStateCard {...emptyState} />;

  if (isPhone)
    return (
      <PhoneStack>
        {shownError && <ErrorBanner {...shownError} />}
        <PhoneCardList
          label={label}
          columns={columns}
          rows={rows}
          getRowKey={getRowKey}
          getRowTestId={getRowTestId}
          renderRowDetail={renderRowDetail}
          selectedRowKey={selectedRowKey}
          isLoading={isLoading}
        />
        {pagination && (
          <PaginationFooter
            pagination={pagination}
            rowCount={rows.length}
            isLoading={isLoading}
            isPhone
          />
        )}
      </PhoneStack>
    );

  return (
    <Stack>
      {shownError && <ErrorBanner {...shownError} />}
      <Surface>
        <StyledTable aria-label={label} aria-busy={isLoading || undefined}>
          <thead>
            <tr>
              {columns.map(({ id, header, align, isHeaderHidden }) => (
                <HeaderCell key={id} scope="col" $isAlignedToEnd={align === "end"}>
                  {isHeaderHidden ? <VisuallyHidden>{header}</VisuallyHidden> : header}
                </HeaderCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? skeletonRows.map((index) => (
                  <tr key={index}>
                    {columns.map(({ id, align }) => (
                      <BodyCell key={id} $isAlignedToEnd={align === "end"} $isTinted={false}>
                        <SkeletonBar aria-hidden="true" />
                      </BodyCell>
                    ))}
                  </tr>
                ))
              : rows.map((row) => {
                  const key = getRowKey(row);
                  const isSelected = key === selectedRowKey;

                  const detail = renderRowDetail?.(row);

                  return (
                    <Fragment key={key}>
                      <BodyRow
                        data-testid={getRowTestId?.(row)}
                        aria-current={isSelected ? "true" : undefined}
                      >
                        {columns.map(({ id, align, render }) => (
                          <BodyCell
                            key={id}
                            $isAlignedToEnd={align === "end"}
                            $isTinted={isSelected}
                          >
                            {render(row)}
                          </BodyCell>
                        ))}
                      </BodyRow>
                      {detail && (
                        <tr>
                          <DetailCell colSpan={columns.length}>{detail}</DetailCell>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
          </tbody>
        </StyledTable>
        {isLoading && <VisuallyHidden role="status">Loading rows</VisuallyHidden>}
        {pagination && (
          <PaginationFooter
            pagination={pagination}
            rowCount={rows.length}
            isLoading={isLoading}
            isPhone={false}
          />
        )}
      </Surface>
    </Stack>
  );
};
